import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import { useCache } from './CacheProvider';

const AuctionContext = React.createContext(null);

const compareAuctionYear = (a, b) => moment(a.date).diff(moment(b.date));

const auctionYearEquals = (year) => (a) => moment(a.date).year() === year;

const useAuctions = () => {
  const { auctions, loading } = React.useContext(AuctionContext);

  const getActiveAuction = () => auctions.filter((a) => a.catalog)[0];

  const getCatalogOrderAuction = () => {
    const qualified = auctions.filter((a) => moment(a.date).add(3, 'weeks') > moment());
    return qualified.length ? qualified[qualified.length - 1] : getActiveAuction();
  };

  const getLatestPrintCatalogAuction = () => {
    const now = moment();
    const upcoming = auctions
      .filter((a) => a.printCatalog && moment(a.date).isAfter(now))
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    if (upcoming.length) return upcoming[0];
    const past = auctions
      .filter((a) => a.printCatalog)
      .sort((a, b) => moment(b.date).diff(moment(a.date)));
    return past[0];
  };

  const getCurrentAuctions = () => {
    const now = moment();
    const thisYear = now.year();
    const nextYear = thisYear + 1;
    const thisYearsAuctions = auctions.filter(auctionYearEquals(thisYear)).sort(compareAuctionYear);
    const nextYearsAuctions = auctions.filter(auctionYearEquals(nextYear)).sort(compareAuctionYear);
    const thisYearsUpcomingAuctions = thisYearsAuctions.filter((a) => moment(a.date).add(1, 'day') > now);
    if (thisYearsUpcomingAuctions.length) {
      return thisYearsAuctions;
    }
    if (nextYearsAuctions.length) {
      return nextYearsAuctions;
    }
    return thisYearsAuctions;
  };

  const getPastAuctions = () => auctions.filter((a) => a.catalog && moment(a.date) < moment());

  const getResults = () => getPastAuctions().filter((a) => a.results);

  return {
    activeAuction: getActiveAuction(),
    archive: getPastAuctions(),
    catalogOrderAuction: getCatalogOrderAuction(),
    currentAuctions: getCurrentAuctions(),
    list: auctions,
    loading,
    latestPrintCatalog: getLatestPrintCatalogAuction(),
    results: getResults(),
  };
};

// eslint-disable-next-line react/display-name
const withAuctions = (Component) => (props) => {
  const auctions = useAuctions();

  return <Component {...props} auctions={auctions} />;
};

const AuctionProvider = ({ children = null }) => {
  const cache = useCache();
  const [auctions, setAuctions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const controller = new AbortController();
    cache.fetchData(setAuctions, setLoading, 'auctions', null, controller);
    return () => controller.abort();
  }, []);

  const value = React.useMemo(() => ({ auctions, loading }), [auctions, loading]);

  return (
    <AuctionContext.Provider value={value}>
      {children}
    </AuctionContext.Provider>
  );
};

AuctionProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export { AuctionProvider, useAuctions, withAuctions };

